import React, { useState, useEffect, useMemo } from 'react';
import { FaTrash, FaEnvelope, FaPhone, FaUser, FaCalendar, FaGlobe, FaFlag, FaFileAlt, FaSearch, FaChevronDown, FaChevronUp, FaTimes, FaComment, FaDownload, FaFilter, FaCheckSquare, FaRegSquare, FaCloudDownloadAlt, FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import { CSVLink } from "react-csv";

const WebinarManagement = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Selection Mode State
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Advanced Filter State
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    taxStatus: 'all',
    location: 'all',
    filedBefore: 'all',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setRegistrations(response.data.data);
      } else {
        throw new Error('Failed to fetch registrations');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to fetch registrations');
      console.error('Error fetching webinar registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/webinar/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        await fetchRegistrations();
        setError('');
      } else {
        throw new Error('Failed to delete registration');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to delete registration');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredRegistrations = useMemo(() => {
    return registrations
      .filter(reg => {
        const query = searchQuery.toLowerCase();
        const matchesSearch = (
          reg.name?.toLowerCase().includes(query) ||
          reg.email?.toLowerCase().includes(query) ||
          reg.countryCode?.toLowerCase().includes(query) ||
          reg.mobile?.toLowerCase().includes(query) ||
          reg.usTaxStatus?.toLowerCase().includes(query) ||
          reg.currentlyLiving?.toLowerCase().includes(query) ||
          reg.filedBefore?.toLowerCase().includes(query) ||
          reg.questions?.toLowerCase().includes(query)
        );

        const matchesStatus = filters.taxStatus === 'all' || reg.usTaxStatus === filters.taxStatus;
        const matchesLocation = filters.location === 'all' || reg.currentlyLiving === filters.location;
        const matchesFiledBefore = filters.filedBefore === 'all' || reg.filedBefore === filters.filedBefore;
        
        let matchesDate = true;
        if (filters.startDate || filters.endDate) {
          const regDate = new Date(reg.createdAt);
          if (filters.startDate && regDate < new Date(filters.startDate)) matchesDate = false;
          if (filters.endDate) {
            const end = new Date(filters.endDate);
            end.setHours(23, 59, 59, 999);
            if (regDate > end) matchesDate = false;
          }
        }

        return matchesSearch && matchesStatus && matchesLocation && matchesFiledBefore && matchesDate;
      })
      .sort((a, b) => {
        let aVal = a[sortField] || '';
        let bVal = b[sortField] || '';
        if (sortField === 'createdAt') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
  }, [registrations, searchQuery, filters, sortField, sortOrder]);

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRegistrations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRegistrations.map(reg => reg._id));
    }
  };

  const csvData = useMemo(() => {
    const dataToExport = selectedIds.length > 0 
      ? registrations.filter(r => selectedIds.includes(r._id))
      : filteredRegistrations;

    return dataToExport.map(reg => ({
      'Name': reg.name,
      'Email': reg.email,
      'Phone': `${reg.countryCode} ${reg.mobile}`,
      'Tax Status': reg.usTaxStatus,
      'Location': reg.currentlyLiving,
      'Filed Before': reg.filedBefore,
      'Questions': reg.questions,
      'Submitted At': formatDate(reg.createdAt)
    }));
  }, [selectedIds, registrations, filteredRegistrations]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaChevronDown className="w-3 h-3 opacity-30" />;
    return sortOrder === 'asc' ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading webinar registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-4 space-y-4">
      {/* Header and Filter Section */}
      <div className="px-4 lg:px-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Webinar Registrations</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400 font-medium">US Tax Return 2025 — Key Changes and Complex Issues</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-bold rounded-xl border border-primary-100 dark:border-primary-800">
              Total: {registrations.length} {registrations.length === 1 ? 'registration' : 'registrations'}
            </span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl animate-in fade-in duration-300">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: registrations.length, color: 'primary', icon: <FaUser className="text-primary-600" /> },
            { label: 'US Citizens', value: registrations.filter(r => r.usTaxStatus === 'US Citizen').length, color: 'blue', icon: <FaFlag className="text-blue-600" /> },
            { label: 'From USA', value: registrations.filter(r => r.currentlyLiving === 'USA').length, color: 'green', icon: <FaGlobe className="text-green-600" /> },
            { label: 'First Time Filers', value: registrations.filter(r => r.filedBefore === 'First time filing').length, color: 'purple', icon: <FaFileAlt className="text-purple-600" /> },
          ].map((stat, i) => (
            <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">{stat.label}</p>
                <div className={`p-2 rounded-lg bg-${stat.color}-50 dark:bg-${stat.color}-900/20`}>
                  {stat.icon}
                </div>
              </div>
              <p className="text-3xl font-black text-gray-900 dark:text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter and Actions Bar */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-grow w-full">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, mobile, tax status, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap border ${
                  showFilters 
                  ? "bg-primary-50 text-primary-600 border-primary-200" 
                  : "bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className={showFilters ? "text-primary-600" : "text-gray-400"} />
                {showFilters ? "Hide Filters" : "Filters"}
              </button>

              <button
                onClick={() => setSelectionMode(!selectionMode)}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${
                  selectionMode 
                  ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" 
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                <FaCheckSquare className={selectionMode ? "animate-pulse" : ""} />
                {selectionMode ? "Selection Active" : "Selection Mode"}
              </button>
              
              <CSVLink
                data={csvData}
                filename={`Webinar_Registrations_${new Date().toISOString().split('T')[0]}.csv`}
                className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap"
              >
                <FaCloudDownloadAlt size={18} />
                Export CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
              </CSVLink>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Tax Status</label>
                <select
                  value={filters.taxStatus}
                  onChange={(e) => setFilters({...filters, taxStatus: e.target.value})}
                  className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">All Statuses</option>
                  <option value="US Citizen">US Citizen</option>
                  <option value="Green Card Holder">Green Card Holder</option>
                  <option value="Non-Resident Alien">Non-Resident Alien</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Location</label>
                <select
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">All Locations</option>
                  <option value="USA">USA</option>
                  <option value="India">India</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">Filed Before</label>
                <select
                  value={filters.filedBefore}
                  onChange={(e) => setFilters({...filters, filedBefore: e.target.value})}
                  className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                >
                  <option value="all">Any History</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="First time filing">First time filing</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">From Date</label>
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest pl-1">To Date</label>
                <div className="relative">
                  <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    className="w-full bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl pl-9 pr-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Width Table Section */}
      <div className="bg-white dark:bg-dark-800 border-y border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead className="bg-gray-50/50 dark:bg-dark-700/50 border-b border-gray-100 dark:border-dark-700">
              <tr>
                {selectionMode && (
                  <th className="px-6 py-4 text-center w-12">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      {selectedIds.length === filteredRegistrations.length && filteredRegistrations.length > 0
                        ? <FaCheckSquare className="w-4 h-4" />
                        : <FaRegSquare className="w-4 h-4" />}
                    </button>
                  </th>
                )}
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-1.5">
                    Name 
                    <SortIcon field="name" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('email')}>
                  <div className="flex items-center gap-1.5">
                    Email 
                    <SortIcon field="email" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Phone</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('usTaxStatus')}>
                  <div className="flex items-center gap-1.5">
                    Tax Status 
                    <SortIcon field="usTaxStatus" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('currentlyLiving')}>
                  <div className="flex items-center gap-1.5">
                    Location 
                    <SortIcon field="currentlyLiving" />
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Filed Before</th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest cursor-pointer group" onClick={() => handleSort('createdAt')}>
                  <div className="flex items-center gap-1.5">
                    Submitted 
                    <SortIcon field="createdAt" />
                  </div>
                </th>
                <th className="px-6 py-4 text-right text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest sticky right-0 bg-gray-50 dark:bg-dark-700 shadow-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-800">
              {filteredRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 9 : 8} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-full">
                        <FaSearch className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
                        {searchQuery ? 'No matches found for your search.' : 'No webinar registrations found.'}
                      </p>
                      <button 
                        onClick={() => {setSearchQuery(''); setFilters({taxStatus:'all', location:'all', filedBefore:'all', startDate:'', endDate:''});}}
                        className="mt-2 text-xs font-bold text-primary-600 hover:underline"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr 
                    key={reg._id} 
                    className={`group transition-all duration-200 hover:bg-primary-50/20 dark:hover:bg-primary-900/10 cursor-pointer ${selectedIds.includes(reg._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(reg._id) : setSelectedRegistration(reg)}
                  >
                    {selectionMode && (
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSelectOne(reg._id)}
                          className={`p-1.5 rounded-lg transition-colors ${selectedIds.includes(reg._id) ? 'text-primary-600 bg-primary-100' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {selectedIds.includes(reg._id) ? <FaCheckSquare className="w-4 h-4" /> : <FaRegSquare className="w-4 h-4" />}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/30 dark:to-primary-900/10 flex items-center justify-center flex-shrink-0 border border-primary-200/50">
                          <FaUser className="text-primary-600 dark:text-primary-400 w-3.5 h-3.5" />
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">{reg.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <a href={`mailto:${reg.email}`} className="text-primary-600 dark:text-primary-400 hover:underline text-xs font-bold" onClick={(e) => e.stopPropagation()}>
                          {reg.email}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FaPhone className="text-gray-400 w-3 h-3" />
                        <span className="text-xs font-semibold text-gray-900 dark:text-white">
                          <span className="text-primary-500 font-bold mr-1">{reg.countryCode}</span>
                          {reg.mobile}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800">
                        {reg.usTaxStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 transition-transform group-hover:translate-x-1">
                      <div className="flex items-center gap-2">
                        <FaGlobe className="text-gray-400 w-3 h-3" />
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{reg.currentlyLiving}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${
                        reg.filedBefore === 'Yes' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-100' :
                        reg.filedBefore === 'No' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-100' :
                        'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-100'
                      }`}>
                        {reg.filedBefore}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FaCalendar className="text-gray-400 w-3 h-3" />
                        <span className="text-xs font-bold text-gray-600 dark:text-gray-400">{formatDate(reg.createdAt)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white dark:bg-dark-800 group-hover:bg-primary-50/20 dark:group-hover:bg-primary-900/10 transition-colors shadow-left">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedRegistration(reg); }}
                          className="px-3 py-1.5 text-xs font-bold bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-600 flex items-center gap-2"
                        >
                          Actions
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedRegistration(null)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-gray-50/50 dark:bg-dark-700/50">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Registration Details</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-widest font-black">US Tax Return 2025 Webinar</p>
              </div>
              <button
                onClick={() => setSelectedRegistration(null)}
                className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-dark-600 rounded-xl transition-all shadow-sm"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaUser className="w-2.5 h-2.5" /> Full Name
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedRegistration.name}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaEnvelope className="w-2.5 h-2.5" /> Email Address
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedRegistration.email}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaPhone className="w-2.5 h-2.5" /> Mobile Number
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">
                    <span className="text-primary-500 mr-1">{selectedRegistration.countryCode}</span>
                    {selectedRegistration.mobile}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaFlag className="w-2.5 h-2.5" /> US Tax Status
                  </label>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                    {selectedRegistration.usTaxStatus}
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaGlobe className="w-2.5 h-2.5" /> Currently Living
                  </label>
                  <p className="text-gray-900 dark:text-white font-bold">{selectedRegistration.currentlyLiving}</p>
                </div>
                <div className="bg-gray-50 dark:bg-dark-700/50 p-4 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-widest">
                    <FaFileAlt className="w-2.5 h-2.5" /> Filed Before
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${
                    selectedRegistration.filedBefore === 'Yes' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                    selectedRegistration.filedBefore === 'No' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                  }`}>
                    {selectedRegistration.filedBefore}
                  </span>
                </div>
              </div>

              {selectedRegistration.questions && (
                <div className="bg-gray-50 dark:bg-dark-700/50 p-5 rounded-2xl border border-gray-100 dark:border-dark-700">
                  <label className="flex items-center gap-1.5 text-[10px] font-black text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-widest">
                    <FaComment className="w-2.5 h-2.5" /> Questions / Comments
                  </label>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap text-sm leading-relaxed font-medium">{selectedRegistration.questions}</p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-primary-50/50 dark:bg-primary-900/10 rounded-2xl border border-primary-100 dark:border-primary-900/20">
                <div className="flex items-center gap-2">
                  <FaCalendar className="text-primary-500 w-3 h-3" />
                  <span className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">Submitted On</span>
                </div>
                <span className="text-sm font-bold text-primary-700 dark:text-primary-400">{formatDate(selectedRegistration.createdAt)}</span>
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3 bg-gray-50/50 dark:bg-dark-700/50">
              <button
                onClick={() => setSelectedRegistration(null)}
                className="px-6 py-2.5 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-xs hover:bg-gray-50 border border-gray-200 dark:border-dark-700 transition-all shadow-sm"
              >
                Close
              </button>
              <button
                onClick={() => { handleDelete(selectedRegistration._id); setSelectedRegistration(null); }}
                className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold text-xs hover:bg-red-700 transition-all shadow-md shadow-red-500/30"
              >
                Delete Registration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebinarManagement;
